import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Send, ArrowLeft, Volume2, Loader2,
  Users, Sparkles, Globe, SlidersHorizontal, Eye, EyeOff,
  Plus, Clock, ChevronRight, Minus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { useWordPopover } from '../shared/reader/useWordPopover';
import SharedWordPopover from '../shared/reader/WordPopover';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = {
  adventurer: 'from-rose-500 to-pink-600',
  guide: 'from-blue-500 to-indigo-600',
  social: 'from-amber-500 to-orange-600',
  scholar: 'from-violet-500 to-purple-600',
  elder: 'from-emerald-500 to-teal-600',
  chef: 'from-red-500 to-rose-600',
  doctor: 'from-cyan-500 to-blue-600',
  merchant: 'from-yellow-500 to-amber-600',
};

const AVATAR_INITIALS = {
  adventurer: 'M', guide: 'L', social: 'N', scholar: 'K',
  elder: 'AR', chef: 'CM', doctor: 'DE', merchant: 'DP',
};

// Hidden translation/pinyin with toggle
function MessageDetails({ transliteration, translation }) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  if (!transliteration && !translation) return null;
  return (
    <div className="mt-1.5">
      {!visible ? (
        <button
          onClick={() => setVisible(true)}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="show-details-btn"
        >
          <Eye className="h-3 w-3" /> {t('conversationPage.showTranslation')}
        </button>
      ) : (
        <div className="space-y-0.5 animate-in fade-in">
          {transliteration && <p className="text-xs opacity-70 font-mono">{transliteration}</p>}
          {translation && <p className="text-xs opacity-60 italic">{translation}</p>}
          <button
            onClick={() => setVisible(false)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <EyeOff className="h-3 w-3" /> {t('conversationPage.hide')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ConversationPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [view, setView] = useState('setup'); // setup | chat | history
  const [characters, setCharacters] = useState({});
  const [scenarios, setScenarios] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [languages, setLanguages] = useState([]);

  // Setup state
  const [selectedChar, setSelectedChar] = useState('mia');
  const [selectedScenario, setSelectedScenario] = useState('cafe');
  const [selectedLang, setSelectedLang] = useState('es');
  const [difficulty, setDifficulty] = useState(0.1);
  const [showTranslit, setShowTranslit] = useState(false);

  // Chat state
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [currentChar, setCurrentChar] = useState(null);
  const [popoverWord, setPopoverWord] = useState(null);
  // Shared popover for unified word lookup
  const popover = useWordPopover({ languageCode: selectedLang });
  const [selectedTokens, setSelectedTokens] = useState({}); // { msgIdx: Set<tokenIdx> }
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [charRes, scenRes, langRes, sessRes] = await Promise.all([
        api.get('/conversation/characters'),
        api.get('/conversation/scenarios'),
        api.get('/languages'),
        api.get('/conversation/sessions'),
      ]);
      setCharacters(charRes.data.characters || {});
      setScenarios(scenRes.data.scenarios || []);
      // Filter to languages with code field and sort by name
      const langs = (langRes.data || []).filter(l => l.code).sort((a, b) => a.name.localeCompare(b.name));
      setLanguages(langs);
      setSessions(sessRes.data.sessions || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startConversation = async () => {
    setStarting(true);
    try {
      const resp = await api.post('/conversation/start', {
        character_id: selectedChar,
        scenario_id: selectedScenario,
        language_code: selectedLang,
        difficulty,
        show_transliteration: showTranslit,
      });
      setSessionId(resp.data.session_id);
      setCurrentChar(resp.data.character);
      setMessages([{ role: 'assistant', ...resp.data.opening_message }]);
      setView('chat');
    } catch (err) {
      toast.error(t('conversationPage.startFailed'));
    } finally { setStarting(false); }
  };

  const resumeSession = async (sid) => {
    try {
      const resp = await api.get(`/conversation/session/${sid}`);
      setSessionId(sid);
      setCurrentChar(resp.data.character);
      setMessages(resp.data.session.messages || []);
      setSelectedLang(resp.data.session.language_code);
      setShowTranslit(resp.data.session.show_transliteration);
      setView('chat');
    } catch { toast.error(t('conversationPage.loadSessionFailed')); }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || sending) return;
    const text = inputText.trim();
    setInputText('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setSending(true);

    try {
      const resp = await api.post('/conversation/message', {
        session_id: sessionId,
        message: text,
        difficulty: difficulty,
      });
      setMessages(prev => [...prev, { role: 'assistant', ...resp.data.message }]);
    } catch {
      toast.error('Failed to get response');
    } finally { setSending(false); }
  };

  const playAudio = (text, langCode) => {
    const url = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(text)}&lang=${langCode || selectedLang}`;
    const audio = new Audio();
    audio.crossOrigin = 'use-credentials';
    audio.src = url;
    audio.play().catch((err) => {
      console.error('Audio playback failed:', err);
      toast.error(t('conversationPage.audioPlayFailed'));
    });
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // SETUP VIEW
  if (view === 'setup') {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="conversation-setup">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{t('conversationPage.title')}</h1>
              <p className="text-xs text-muted-foreground">{t('conversationPage.subtitle')}</p>
            </div>
          </div>
          {sessions.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setView('history')} className="gap-1">
              <Clock className="h-4 w-4" /> {t('conversationPage.history')}
            </Button>
          )}
        </div>

        {/* Language */}
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('conversationPage.language')}</label>
          <select
            value={selectedLang}
            onChange={e => setSelectedLang(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-sm max-h-60 overflow-y-auto"
            data-testid="conv-lang-select"
          >
            {languages.map(l => (
              <option key={l.language_id || l.code} value={l.code}>{l.name} ({l.code})</option>
            ))}
          </select>
        </div>

        {/* Character Selection */}
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('conversationPage.character')}</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(characters).map(([id, char]) => (
              <button
                key={id}
                onClick={() => setSelectedChar(id)}
                className={`p-3 rounded-xl border text-center transition-all ${
                  selectedChar === id
                    ? 'border-indigo-500 bg-indigo-500/10 ring-1 ring-indigo-500'
                    : 'border-border hover:border-indigo-400/50'
                }`}
                data-testid={`char-${id}`}
              >
                {char.avatar_url ? (
                  <img
                    src={`${BACKEND_URL}${char.avatar_url}`}
                    alt={char.name}
                    className="w-10 h-10 mx-auto rounded-full object-cover shadow-md"
                    loading="lazy"
                  />
                ) : (
                  <div className={`w-10 h-10 mx-auto rounded-full bg-gradient-to-br ${AVATAR_COLORS[char.avatar] || 'from-slate-500 to-slate-600'} flex items-center justify-center text-white font-bold text-sm`}>
                    {AVATAR_INITIALS[char.avatar] || char.name[0]}
                  </div>
                )}
                <p className="text-xs font-medium mt-2 truncate">{char.name}</p>
              </button>
            ))}
          </div>
          {characters[selectedChar] && (
            <p className="text-xs text-muted-foreground mt-2 italic">{characters[selectedChar].personality.substring(0, 100)}...</p>
          )}
        </div>

        {/* Scenario */}
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-2 block">Scenario</label>
          <div className="grid grid-cols-2 gap-2">
            {scenarios.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedScenario(s.id)}
                className={`p-2.5 rounded-lg border text-left transition-all ${
                  selectedScenario === s.id
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-border hover:border-indigo-400/50'
                }`}
                data-testid={`scenario-${s.id}`}
              >
                <p className="text-sm font-medium">{s.name}</p>
                <p className="text-[10px] text-muted-foreground">{s.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <SlidersHorizontal className="h-3.5 w-3.5" /> {t('conversationPage.difficulty')}
            </label>
            <span className="text-xs font-mono text-indigo-400">{Math.round(difficulty * 100)}%</span>
          </div>
          <input
            type="range" min="0" max="0.5" step="0.05"
            value={difficulty}
            onChange={e => setDifficulty(parseFloat(e.target.value))}
            className="w-full accent-indigo-500"
            data-testid="difficulty-slider"
          />
          <div className="flex items-center justify-between">
            <label className="text-sm text-muted-foreground">Show transliteration</label>
            <button
              onClick={() => setShowTranslit(!showTranslit)}
              className={`p-1.5 rounded-md transition-colors ${showTranslit ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground'}`}
              data-testid="translit-toggle"
            >
              {showTranslit ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Start Button */}
        <Button
          onClick={startConversation}
          disabled={starting}
          className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
          data-testid="start-conversation"
        >
          {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
          {t('conversationPage.startConversation')}
        </Button>
      </div>
    );
  }

  // HISTORY VIEW
  if (view === 'history') {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="conversation-history">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('setup')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">Conversation History</h1>
        </div>
        {sessions.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No conversations yet</p>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => (
              <button
                key={s.session_id}
                onClick={() => resumeSession(s.session_id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-indigo-500/50 transition-all text-left"
              >
                {characters[s.character_id]?.avatar_url ? (
                  <img
                    src={`${BACKEND_URL}${characters[s.character_id].avatar_url}`}
                    alt={s.character_name || ''}
                    className="w-9 h-9 rounded-full object-cover shadow-sm"
                    loading="lazy"
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${AVATAR_COLORS[characters[s.character_id]?.avatar] || 'from-slate-500 to-slate-600'} flex items-center justify-center text-white text-xs font-bold`}>
                    {s.character_name?.[0] || '?'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.character_name} — {s.scenario_name || t('conversationPage.freeChat')}</p>
                  <p className="text-[10px] text-muted-foreground">{t('conversationPage.messageCount', { count: s.message_count })} • {new Date(s.updated_at).toLocaleDateString()}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
        <Button onClick={() => setView('setup')} variant="outline" className="w-full gap-2">
          <Plus className="h-4 w-4" /> {t('conversationPage.newConversation')}
        </Button>
      </div>
    );
  }

  // CHAT VIEW
  const adjustDifficulty = (delta) => {
    setDifficulty(prev => Math.max(0, Math.min(0.5, Math.round((prev + delta) * 100) / 100)));
  };

  const handleWordClick = (word) => {
    const cleaned = word.replace(/[.,!?;:"""''«»¿¡—\-()[\]{}]/g, '').trim();
    if (cleaned.length > 0) {
      popover.openForTerm(cleaned);
    }
  };

  const handleTokenClick = (msgIdx, tokenIdx, token) => {
    if (!token.is_word) return;

    setSelectedTokens(prev => {
      const key = msgIdx;
      const current = new Set(prev[key] || []);

      if (current.has(tokenIdx)) {
        // Deselect
        current.delete(tokenIdx);
      } else {
        // If selecting adjacent tokens, allow multi-select (expression)
        if (current.size === 0) {
          current.add(tokenIdx);
        } else {
          // Check if adjacent to existing selection
          const sorted = [...current].sort((a, b) => a - b);
          const min = sorted[0];
          const max = sorted[sorted.length - 1];
          if (tokenIdx >= min - 1 && tokenIdx <= max + 1) {
            current.add(tokenIdx);
          } else {
            // Not adjacent — start new selection
            return { ...prev, [key]: new Set([tokenIdx]) };
          }
        }
      }

      if (current.size === 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: current };
    });
  };

  const getSelectedExpression = (msgIdx, tokens) => {
    const sel = selectedTokens[msgIdx];
    if (!sel || sel.size === 0) return null;
    const sorted = [...sel].sort((a, b) => a - b);
    return sorted.map(i => tokens[i]?.text || '').join('');
  };

  const confirmSelection = (msgIdx, tokens) => {
    const expression = getSelectedExpression(msgIdx, tokens);
    if (!expression) return;

    if (selectedTokens[msgIdx]?.size === 1) {
      // Single word — open shared popover
      popover.openForTerm(expression);
    } else {
      // Multi-word — save as expression
      api.post('/conversation/save-expression', {
        expression,
        language_code: selectedLang,
        context: messages[msgIdx]?.content || messages[msgIdx]?.text || '',
      }).then(() => {
        toast.success(`Expression saved: "${expression}"`);
      }).catch(() => toast.error('Failed to save expression'));
    }
    // Clear selection
    setSelectedTokens(prev => { const n = { ...prev }; delete n[msgIdx]; return n; });
  };

  const renderTokenizedText = (tokens, msgIdx) => {
    const sel = selectedTokens[msgIdx] || new Set();
    return (
      <span>
        {tokens.map((tok, i) => {
          if (!tok.is_word) return <span key={i}>{tok.text}</span>;
          const isSelected = sel.has(i);
          const isUnknown = tok.is_known === false;
          return (
            <span
              key={i}
              onClick={() => handleTokenClick(msgIdx, i, tok)}
              className={`cursor-pointer rounded px-0.5 transition-all ${
                isSelected
                  ? 'bg-indigo-500/30 text-indigo-200 ring-1 ring-indigo-500/50'
                  : isUnknown
                    ? 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 underline decoration-dotted decoration-amber-400/50 underline-offset-2'
                    : 'hover:bg-indigo-500/20 hover:text-indigo-300 underline decoration-dotted decoration-indigo-500/30 underline-offset-2'
              }`}
              data-testid={`token-${msgIdx}-${i}`}
            >
              {tok.text}
            </span>
          );
        })}
        {sel.size > 0 && (
          <button
            onClick={() => confirmSelection(msgIdx, tokens)}
            className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
            data-testid={`confirm-selection-${msgIdx}`}
          >
            {sel.size === 1 ? 'Look up' : 'Save expression'}
          </button>
        )}
      </span>
    );
  };

  const renderClickableText = (text, langCode) => {
    if (!text) return null;
    const parts = text.split(/(\s+|[.,!?;:"""''«»¿¡—\-()[\]{}])/);
    return parts.map((part, i) => {
      const isWord = /\p{L}/u.test(part);
      if (isWord) {
        return (
          <span
            key={i}
            onClick={() => handleWordClick(part)}
            className="cursor-pointer hover:bg-indigo-500/20 hover:text-indigo-300 rounded px-0.5 transition-colors underline decoration-dotted decoration-indigo-500/30 underline-offset-2"
            data-testid={`chat-word-${i}`}
          >
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Get language_id for current language
  const currentLangId = languages.find(l => l.code === selectedLang)?.language_id || '';

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] max-w-2xl mx-auto" data-testid="conversation-chat">
      {/* Header */}
      <div className="flex items-center gap-3 p-3 border-b border-border flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => { setView('setup'); fetchData(); }}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {currentChar && (
          <>
            {currentChar.avatar_url ? (
              <img
                src={`${BACKEND_URL}${currentChar.avatar_url}`}
                alt={currentChar.name}
                className="w-9 h-9 rounded-full object-cover shadow-sm"
                loading="lazy"
              />
            ) : (
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${AVATAR_COLORS[currentChar.avatar] || 'from-slate-500 to-slate-600'} flex items-center justify-center text-white font-bold text-sm`}>
                {AVATAR_INITIALS[currentChar.avatar] || currentChar.name[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{currentChar.name}</p>
              <p className="text-[10px] text-muted-foreground">{selectedLang.toUpperCase()}</p>
            </div>
          </>
        )}
        {/* Difficulty +/- controls */}
        <div className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
          <button
            onClick={() => adjustDifficulty(-0.1)}
            className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
            title={t('conversationPage.easier')}
            data-testid="difficulty-minus"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="text-[10px] font-mono w-8 text-center text-indigo-400" data-testid="difficulty-display">
            {Math.round(difficulty * 100)}%
          </span>
          <button
            onClick={() => adjustDifficulty(0.1)}
            className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
            title={t('conversationPage.harder')}
            data-testid="difficulty-plus"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : 'bg-muted border border-border rounded-bl-sm'
            }`}>
              <p className="text-sm leading-relaxed">
                {msg.role === 'assistant'
                  ? (msg.tokens && msg.tokens.length > 0
                      ? renderTokenizedText(msg.tokens, i)
                      : renderClickableText(msg.content || msg.text, selectedLang))
                  : (msg.content || msg.text)}
              </p>
              {/* Transliteration + Translation — hidden by default with toggle */}
              {msg.role === 'assistant' && (msg.transliteration || msg.translation) && (
                <MessageDetails transliteration={msg.transliteration} translation={msg.translation} />
              )}
              {msg.new_words && msg.new_words.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10" data-testid="new-words-list">
                  <p className="text-[9px] uppercase tracking-wide text-amber-300/80 mb-1">{t('conversationPage.newWords')}</p>
                  {msg.new_words.map((w, j) => (
                    <span key={j} className="inline-block text-[10px] bg-amber-500/20 text-amber-300 rounded px-1.5 py-0.5 mr-1 mb-0.5">
                      {w.word}: {w.meaning}
                    </span>
                  ))}
                </div>
              )}
              {msg.foreign_words && msg.foreign_words.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10" data-testid="foreign-words-list">
                  <p className="text-[9px] uppercase tracking-wide text-sky-300/80 mb-1">In {selectedLang.toUpperCase()}</p>
                  {msg.foreign_words.map((w, j) => (
                    <div key={j} className="text-[11px] text-sky-200/90 mb-0.5">
                      <span className="opacity-60">"{w.word}"</span> → <span className="font-semibold text-sky-300">{w.target}</span>
                      {w.meaning && <span className="opacity-50 ml-1">({w.meaning})</span>}
                    </div>
                  ))}
                </div>
              )}
              {msg.corrections && msg.corrections.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10" data-testid="corrections-list">
                  <p className="text-[9px] uppercase tracking-wide text-rose-300/80 mb-1">Corrections</p>
                  {msg.corrections.map((c, j) => (
                    <div key={j} className="text-[11px] mb-0.5">
                      <span className="line-through opacity-60 text-rose-300/80">{c.original}</span>
                      {' → '}
                      <span className="font-semibold text-emerald-300">{c.correct}</span>
                      {c.explanation && <div className="text-[10px] italic opacity-70 pl-2">{c.explanation}</div>}
                    </div>
                  ))}
                </div>
              )}
              {msg.role === 'assistant' && (msg.content || msg.text) && (
                <button
                  onClick={() => playAudio(msg.content || msg.text, selectedLang)}
                  className="mt-1 p-1 rounded-full hover:bg-white/10 transition-colors"
                  title={t('conversationPage.listenFull')}
                >
                  <Volume2 className="h-3.5 w-3.5 opacity-50 hover:opacity-100" />
                </button>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-muted border border-border rounded-2xl rounded-bl-sm px-4 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={t('conversationPage.typePlaceholder', { lang: selectedLang.toUpperCase() })}
            className="flex-1 px-4 py-2.5 rounded-full bg-muted border border-border text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={sending || !inputText.trim()}
            size="icon"
            className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-10 w-10"
            data-testid="send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Shared Word Popover */}
      {popover.selectedToken && popover.popoverPos && (
        <SharedWordPopover
          style={popover.popoverPos}
          wordData={popover.wordData}
          wordLoading={popover.wordLoading}
          language={{ code: selectedLang }}
          token={popover.selectedToken}
          onClose={() => popover.close()}
          onSetStatus={(newStatus) => {
            toast.success(`Word marked as ${newStatus}`);
            popover.close();
          }}
          locked={popover.wordData?.locked}
          lockedFields={popover.wordData?.locked_fields || []}
          onRelatedWordClick={(term) => popover.openForTerm(term)}
          isNewEntry={popover.isNewEntry}
        />
      )}
    </div>
  );
}
